import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import {
  PerformersCard as BasePerformersCard,
  PerformersCardProps as BasePerformersCardProps,
} from '../../../../../components/RealTimeOptimization/Performers/PerformersCard';
import { AIInsights } from '@features/ai-insights';

type LiftOption = BasePerformersCardProps<
  PerformersCardProps['selectedReport'],
  PerformersCardProps['selectedReport']['subdimensions'][number],
  'visit' | 'sales'
>['liftOptions'][number];

const visitLiftOption = {
  value: 'visit',
  name: 'Visit',
  chartLabel: 'Incremental Visit Lift',
} as const satisfies LiftOption;

const salesLiftOption = {
  value: 'sales',
  name: 'Sales',
  chartLabel: 'Incremental Sales Lift',
} as const satisfies LiftOption;

const buildLiftOptions = (
  hasSalesData: boolean
): BasePerformersCardProps<
  PerformersCardProps['selectedReport'],
  PerformersCardProps['selectedReport']['subdimensions'][number],
  'visit' | 'sales'
>['liftOptions'] => [
  ...(hasSalesData ? [salesLiftOption] : []),
  visitLiftOption,
];

type PerformersCardProps = {
  selectedReport: Pick<Lci3Report, 'subdimensions'>;
  allReports: Lci3Report[];
  aiInsights: AIInsights | null;
};

export const PerformersCard = (props: PerformersCardProps) => {
  const hasSalesData = props.allReports.some(report => report.salesMetrics);

  return (
    <BasePerformersCard
      reports={props.allReports}
      reportDates={props.allReports.map(report => report.reportDate)}
      filterReports={liftType =>
        liftType === 'sales'
          ? props.allReports.filter(report => report.salesMetrics)
          : props.allReports
      }
      currentSubdimensions={props.selectedReport.subdimensions}
      getValueByLiftType={{
        sales: subdimension =>
          subdimension.salesMetrics?.incrementalSalesLift.total ?? null,
        visit: subdimension => subdimension.visitMetrics.incrementalLift,
      }}
      getOverallValueByLiftType={{
        sales: report =>
          report.salesMetrics?.incrementalSalesLift.total ?? null,
        visit: report => report.visitMetrics.incrementalLift,
      }}
      getSubdimensionsFromReport={report => report.subdimensions}
      defaultLiftType={hasSalesData ? 'sales' : 'visit'}
      liftOptions={buildLiftOptions(hasSalesData)}
      aiInsights={props.aiInsights}
    />
  );
};
